import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Calendar,
  Download,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Wallet,
  Users,
  ArrowRightLeft,
  Inbox,
  FileText,
  FileSpreadsheet,
  Moon,
  Flame,
  Sun,
  Compass,
  Flag,
  DollarSign
} from "lucide-react";
import { Modal } from "../ui/Modal";
import { Toast } from "../ui/Toast";
import { Badge } from "../ui/Badge";
import NovoEventoService from "../../services/Eventos/NovoEventoService";
import InscricaoService from "../../services/Inscricoes/InscricaoService";
import { formatDateBR } from "../../utils/dateUtils";
import { API_BASE_URL } from "../../services/config";

// Componente de Sparkline suave para os cards
const Sparkline = ({ color = "#8AE600", type = "green" }) => {
  let d = "M2 18 C 15 18, 22 6, 35 12 C 48 18, 55 4, 68 8";
  if (type === "blue") {
    d = "M2 17 C 12 17, 24 19, 36 10 C 48 2, 58 16, 68 8";
  } else if (type === "purple") {
    d = "M2 18 C 14 18, 20 12, 34 16 C 46 20, 56 6, 68 10";
  }

  return (
    <svg width="68" height="22" viewBox="0 0 70 24" fill="none" className="overflow-visible">
      <path
        d={d}
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

function FinanceiroOrganizador() {
  const [financeiro, setFinanceiro] = useState([]);
  const [aCarregar, setACarregar] = useState(true);
  const [filtroGlobal, setFiltroGlobal] = useState("");
  const [detalhesDialog, setDetalhesDialog] = useState(false);
  const [filtroMenuVisible, setFiltroMenuVisible] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState("recentes");
  const [corridaSelecionada, setCorridaSelecionada] = useState(null);
  const [inscricoesDetalhes, setInscricoesDetalhes] = useState([]);
  const [meiosPagamento, setMeiosPagamento] = useState(null);
  const [aCarregarDetalhes, setACarregarDetalhes] = useState(false);
  const [taxasMap, setTaxasMap] = useState({});

  // Paginação simples
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 7;

  const toastRef = useRef(null);
  const exportRef = useRef(null);

  const eventoService = useMemo(() => new NovoEventoService(), []);
  const inscricaoService = useMemo(() => new InscricaoService(), []);

  // Fechar menu de exportação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const buscaFinanceiro = useCallback(async () => {
    setACarregar(true);
    try {
      const userId = sessionStorage.getItem("brv-user-id");
      const response = await eventoService.getFinanceiroCorrida(userId);
      if (!response.ok) {
        throw new Error(`Erro de HTTP: ${response.status}`);
      }
      const dat = await response.json();
      const lista = Array.isArray(dat) ? dat : [];
      setFinanceiro(lista);

      // Carrega as taxas configuradas no sistema para as corridas
      try {
        const token = sessionStorage.getItem("brv-user-token");
        const headers = {
          "Content-Type": "application/json; charset=UTF-8",
          ...(token ? { Authorization: `Token ${token}` } : {})
        };

        const fetchTaxasUrl = async (url) => {
          const res = await fetch(url, { headers });
          if (!res.ok && res.status === 404 && url.includes("/core/fin/")) {
            return fetch(url.replace("/core/fin/", "/fin/"), { headers });
          }
          return res;
        };

        const resTaxas = await fetchTaxasUrl(`${API_BASE_URL}/core/fin/taxas/`);
        const mapa = {};
        if (resTaxas.ok) {
          const dataTaxas = await resTaxas.json();
          const taxasLista = Array.isArray(dataTaxas) ? dataTaxas : (dataTaxas?.results || []);
          taxasLista.forEach((t) => {
            const id = t.id_corrida || (typeof t.corrida === "object" ? t.corrida?.id : t.corrida) || t.evento;
            if (id) {
              mapa[id] = t;
            }
          });
        }

        // Para corridas que não vieram no list geral de taxas, tenta buscar individualmente
        const corridasSemTaxa = lista.filter((c) => {
          const id = c.id_corrida ?? c.id;
          return id && !mapa[id];
        });

        if (corridasSemTaxa.length > 0) {
          await Promise.allSettled(
            corridasSemTaxa.map(async (c) => {
              const id = c.id_corrida ?? c.id;
              try {
                const resSingle = await fetchTaxasUrl(`${API_BASE_URL}/core/fin/taxas/corrida/${id}/`);
                if (resSingle.ok) {
                  const d = await resSingle.json();
                  if (d && (d.valor !== undefined || d.taxa !== undefined || d.valor_fixo !== undefined || d.valor_taxa !== undefined)) {
                    mapa[id] = d;
                  }
                }
              } catch {
                // Silencioso se não houver taxa específica
              }
            })
          );
        }

        setTaxasMap(mapa);
      } catch (errTaxas) {
        console.warn("Aviso ao carregar taxas das corridas:", errTaxas);
      }
    } catch (error) {
      console.error("Erro ao carregar dados financeiros:", error);
      setFinanceiro([]);
    } finally {
      setACarregar(false);
    }
  }, [eventoService]);

  useEffect(() => {
    buscaFinanceiro();
  }, [buscaFinanceiro]);

  const abrirDetalhesCorrida = async (rowData) => {
    setCorridaSelecionada(rowData);
    setDetalhesDialog(true);
    setACarregarDetalhes(true);
    setInscricoesDetalhes([]);
    setMeiosPagamento(null);

    const corridaId = rowData.id_corrida ?? rowData.id ?? null;
    if (corridaId) {
      try {
        const [resInscricoes, resMeios] = await Promise.all([
          inscricaoService.getInscricaoPerCorrida(corridaId, 1, 5),
          inscricaoService.getCounttMeiosPagamentoPerCorrida(corridaId)
        ]);

        if (resInscricoes.ok) {
          const datInsc = await resInscricoes.json();
          const lista = Array.isArray(datInsc) ? datInsc : (Array.isArray(datInsc?.results) ? datInsc.results : []);
          setInscricoesDetalhes(lista);
        }
        if (resMeios.ok) {
          const datMeios = await resMeios.json();
          setMeiosPagamento(datMeios);
        }
      } catch (err) {
        console.error("Erro ao buscar detalhes da inscrição:", err);
      } finally {
        setACarregarDetalhes(false);
      }
    } else {
      setACarregarDetalhes(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return "R$ 0,00";
    }
    const numValue = Number(value);
    return numValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const formatDate = (value) => {
    return formatDateBR(value);
  };

  // Cálculos de Métricas Gerais
  const totalInscritos = financeiro.reduce((acc, item) => acc + (Number(item.total_inscritos) || 0), 0);
  const totalAcumulado = financeiro.reduce((acc, item) => acc + (Number(item.valor_acumulado) || 0), 0);
  const totalRepassar = financeiro.reduce((acc, item) => acc + (Number(item.valor_repassar) || ((Number(item.valor_acumulado) || 0) * 0.1) || 0), 0);
  const totalLiquido = financeiro.reduce((acc, item) => acc + (Number(item.valor_liquido) || ((Number(item.valor_acumulado) || 0) * 0.9) || 0), 0);

  // Filtros e Ordenação
  const dadosFiltrados = useMemo(() => {
    let items = [...financeiro];

    if (filtroGlobal.trim()) {
      const termo = filtroGlobal.toLowerCase();
      items = items.filter((i) => (i.nome_corrida || "").toLowerCase().includes(termo));
    }

    if (ordenarPor === "acumulado_desc") {
      items.sort((a, b) => (Number(b.valor_acumulado) || 0) - (Number(a.valor_acumulado) || 0));
    } else if (ordenarPor === "inscritos_desc") {
      items.sort((a, b) => (Number(b.total_inscritos) || 0) - (Number(a.total_inscritos) || 0));
    } else if (ordenarPor === "nome_asc") {
      items.sort((a, b) => (a.nome_corrida || "").localeCompare(b.nome_corrida || ""));
    }

    return items;
  }, [financeiro, filtroGlobal, ordenarPor]);

  // Paginação
  const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina) || 1;
  const inicioIndice = (paginaAtual - 1) * itensPorPagina;
  const dadosPaginados = dadosFiltrados.slice(inicioIndice, inicioIndice + itensPorPagina);
  const totalMostrado = dadosFiltrados.length;
  const inicioItem = totalMostrado > 0 ? inicioIndice + 1 : 0;
  const fimItem = Math.min(inicioIndice + itensPorPagina, totalMostrado);

  const handleExportCSV = () => {
    const cabecalho = "Corrida;Inscritos;Valor Acumulado;Comissao;Valor Liquido\n";
    const linhas = dadosFiltrados
      .map((d) => `"${d.nome_corrida || ""}";${d.total_inscritos || 0};"${d.valor_acumulado || 0}";"${d.valor_repassar || 0}";"${d.valor_liquido || 0}"`)
      .join("\n");
    const blob = new Blob([cabecalho + linhas], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio_financeiro_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
    toastRef.current?.show({ severity: "success", summary: "Exportado", detail: "Relatório CSV gerado com sucesso!", life: 3000 });
  };

  const handleExportExcel = () => {
    setExportMenuOpen(false);
    toastRef.current?.show({ severity: "info", summary: "Exportação", detail: "Gerando arquivo de exportação...", life: 2500 });
  };

  // Helper para ícone visual temático da corrida caso imagem não exista
  const getRaceVisualIcon = (nomeCorrida) => {
    const nome = (nomeCorrida || "").toLowerCase();
    if (nome.includes("night") || nome.includes("noite") || nome.includes("luau")) {
      return { Icon: Moon, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" };
    }
    if (nome.includes("blaze") || nome.includes("fire") || nome.includes("fogo")) {
      return { Icon: Flame, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20" };
    }
    if (nome.includes("sunset") || nome.includes("sol") || nome.includes("verao")) {
      return { Icon: Sun, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" };
    }
    if (nome.includes("trail") || nome.includes("curiaú") || nome.includes("montanha") || nome.includes("desafio")) {
      return { Icon: Compass, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" };
    }
    return { Icon: Flag, color: "text-urb-primary", bg: "bg-urb-primary/10 border-urb-primary/20" };
  };

  const getTaxaCorrida = useCallback((rowData) => {
    if (!rowData) return null;
    const idCorrida = rowData.id_corrida ?? rowData.id;

    // 1. Procura no mapa de taxas obtido dos endpoints de taxas
    const taxaDoMapa = idCorrida ? taxasMap[idCorrida] : null;
    if (taxaDoMapa) {
      if (typeof taxaDoMapa === "object") {
        const val = taxaDoMapa.valor ?? taxaDoMapa.valor_fixo ?? taxaDoMapa.taxa ?? taxaDoMapa.valor_taxa;
        if (val !== undefined && val !== null && val !== "") {
          return Number(val);
        }
      } else if (typeof taxaDoMapa === "number") {
        return taxaDoMapa;
      }
    }

    // 2. Procura nos campos diretos do rowData
    const propTaxa =
      rowData.taxa_valor ??
      rowData.valor_taxa ??
      rowData.taxa_fixa ??
      rowData.valor_fixo ??
      rowData.taxa_urbrace ??
      rowData.taxa_definida ??
      rowData.taxa;

    if (propTaxa !== undefined && propTaxa !== null && propTaxa !== "") {
      if (typeof propTaxa === "object") {
        const val = propTaxa.valor ?? propTaxa.valor_fixo ?? propTaxa.taxa ?? propTaxa.valor_taxa;
        if (val !== undefined && val !== null && val !== "") {
          return Number(val);
        }
      } else if (!isNaN(Number(propTaxa))) {
        return Number(propTaxa);
      }
    }

    // 3. Se houver taxa_porcentagem definida
    if (rowData.taxa_porcentagem !== undefined && rowData.taxa_porcentagem !== null && rowData.taxa_porcentagem !== "") {
      return `${rowData.taxa_porcentagem}%`;
    }

    // 4. Se houver valor_repassar e total_inscritos > 0
    if (rowData.valor_repassar && rowData.total_inscritos && Number(rowData.total_inscritos) > 0) {
      const calculada = Number(rowData.valor_repassar) / Number(rowData.total_inscritos);
      if (calculada > 0 && !isNaN(calculada)) {
        return calculada;
      }
    }

    // 5. Fallback padrão da Urbrace
    return 7.90;
  }, [taxasMap]);

  const formatTaxa = useCallback((taxa) => {
    if (taxa === null || taxa === undefined || taxa === "") return "R$ 7,90";
    if (typeof taxa === "string") {
      if (taxa.includes("%") || taxa.startsWith("R$")) return taxa;
      const num = Number(taxa.replace(",", "."));
      if (!isNaN(num)) return formatCurrency(num);
      return taxa;
    }
    if (typeof taxa === "number") {
      return formatCurrency(taxa);
    }
    return "R$ 7,90";
  }, []);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 text-urb-text">
      <Toast ref={toastRef} />

      {/* Header Superior da Página */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            Financeiro
          </h1>
          <p className="text-sm text-urb-text-muted mt-1">
            Acompanhe os resultados e o fluxo financeiro das suas corridas.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Seletor de Período de Datas */}
          <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-urb-card border border-urb-border rounded-xl text-xs font-semibold text-slate-300 shadow-sm">
            <Calendar className="w-4 h-4 text-urb-text-muted" />
            <span>01/01/2026 - 31/12/2026</span>
            <ChevronDown className="w-3.5 h-3.5 text-urb-text-muted" />
          </div>

          {/* Botão de Exportação com Dropdown */}
          <div className="relative" ref={exportRef}>
            <button
              type="button"
              onClick={() => setExportMenuOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 px-3.5 py-2 bg-urb-card hover:bg-urb-card-light border border-urb-border hover:border-urb-primary/50 rounded-xl text-xs font-semibold text-slate-200 transition-colors shadow-sm cursor-pointer"
            >
              <Download className="w-4 h-4 text-urb-text-muted" />
              <span>Exportar</span>
              <ChevronDown className="w-3.5 h-3.5 text-urb-text-muted" />
            </button>

            {exportMenuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-urb-card border border-urb-border rounded-xl shadow-xl z-30 py-1.5 animate-in fade-in zoom-in-95 duration-150">
                <button
                  type="button"
                  onClick={handleExportCSV}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-slate-300 hover:text-white hover:bg-urb-card-light transition-colors cursor-pointer bg-transparent border-none"
                >
                  <FileText className="w-4 h-4 text-urb-primary" />
                  <span>Exportar para CSV</span>
                </button>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-slate-300 hover:text-white hover:bg-urb-card-light transition-colors cursor-pointer bg-transparent border-none"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                  <span>Exportar para Excel (XLS)</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Grid: 4 Cards de Métricas Principais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Card 1: Valor Acumulado */}
        <div className="bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-2xl p-5 flex flex-col justify-between transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold tracking-wider text-urb-text-muted uppercase">
                VALOR ACUMULADO
              </span>
              <span title="Valor bruto total arrecadado" className="text-slate-500 text-xs cursor-help">
                ⓘ
              </span>
            </div>
            <div className="w-8 h-8 rounded-full bg-urb-primary/10 border border-urb-primary/20 flex items-center justify-center text-urb-primary font-black text-sm">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>

          <div className="my-3">
            <span className="text-2xl sm:text-3xl font-extrabold text-urb-primary tracking-tight">
              {formatCurrency(totalAcumulado)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-urb-text-muted">
              Total bruto de inscrições
            </span>
            <Sparkline color="#8AE600" type="green" />
          </div>
        </div>

        {/* Card 2: Valor a Repassar */}
        <div className="bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-2xl p-5 flex flex-col justify-between transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold tracking-wider text-urb-text-muted uppercase">
                VALOR A REPASSAR
              </span>
              <span title="Comissão devida da taxa de serviço" className="text-slate-500 text-xs cursor-help">
                ⓘ
              </span>
            </div>
            <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
              <ArrowRightLeft className="w-4 h-4" />
            </div>
          </div>

          <div className="my-3">
            <span className="text-2xl sm:text-3xl font-extrabold text-blue-400 tracking-tight">
              {formatCurrency(totalRepassar)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-urb-text-muted">
              Comissão URB RACE (R$ 7,90)
            </span>
            <Sparkline color="#3b82f6" type="blue" />
          </div>
        </div>

        {/* Card 3: Valor Líquido */}
        <div className="bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-2xl p-5 flex flex-col justify-between transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold tracking-wider text-urb-text-muted uppercase">
                VALOR LÍQUIDO
              </span>
              <span title="Valor líquido disponível para repasse do organizador" className="text-slate-500 text-xs cursor-help">
                ⓘ
              </span>
            </div>
            <div className="w-8 h-8 rounded-full bg-urb-primary/10 border border-urb-primary/20 flex items-center justify-center text-urb-primary">
              <Wallet className="w-4 h-4" />
            </div>
          </div>

          <div className="my-3">
            <span className="text-2xl sm:text-3xl font-extrabold text-urb-primary tracking-tight">
              {formatCurrency(totalLiquido)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-urb-text-muted">
              Disponível para repasse
            </span>
            <Sparkline color="#8AE600" type="green" />
          </div>
        </div>

        {/* Card 4: Total de Inscrições */}
        <div className="bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-2xl p-5 flex flex-col justify-between transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold tracking-wider text-urb-text-muted uppercase">
                TOTAL DE INSCRIÇÕES
              </span>
              <span title="Número acumulado de inscrições confirmadas" className="text-slate-500 text-xs cursor-help">
                ⓘ
              </span>
            </div>
            <div className="w-8 h-8 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
              <Users className="w-4 h-4" />
            </div>
          </div>

          <div className="my-3">
            <span className="text-2xl sm:text-3xl font-extrabold text-purple-400 tracking-tight">
              {totalInscritos}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-urb-text-muted">
              Em todas as corridas
            </span>
            <Sparkline color="#c084fc" type="purple" />
          </div>
        </div>
      </div>

      {/* Container Principal da Tabela: Resumo por Corrida */}
      <div className="bg-urb-card border border-urb-border rounded-2xl p-4 sm:p-6 mb-6">
        {/* Header Interno do Card */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-bold text-white tracking-tight">
            Resumo por Corrida
          </h2>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-between sm:justify-end">
            {/* Campo de Busca */}
            <div className="relative flex items-center w-full sm:w-60">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 pointer-events-none" />
              <input
                type="text"
                value={filtroGlobal}
                onChange={(e) => {
                  setFiltroGlobal(e.target.value);
                  setPaginaAtual(1);
                }}
                placeholder="Buscar corrida..."
                className="w-full pl-9 pr-3.5 py-1.5 bg-urb-card-light border border-urb-border rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-urb-primary/60 transition-colors"
              />
            </div>

            {/* Botão Filtros */}
            <button
              type="button"
              onClick={() => setFiltroMenuVisible(!filtroMenuVisible)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors cursor-pointer shrink-0 ${
                filtroMenuVisible
                  ? "bg-urb-primary/10 border-urb-primary/40 text-urb-primary"
                  : "bg-urb-card-light hover:bg-urb-card border-urb-border text-slate-300"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>Filtros</span>
            </button>
          </div>
        </div>

        {/* Dropdown / Menu de Filtros Secundário (toggleable) */}
        {filtroMenuVisible && (
          <div className="flex flex-wrap items-center gap-2 p-3 mb-4 bg-urb-card-light/60 border border-urb-border/80 rounded-xl animate-in fade-in duration-150">
            <span className="text-xs font-semibold text-slate-400 mr-1">Ordenar por:</span>
            {[
              { id: "recentes", label: "Mais recentes" },
              { id: "acumulado_desc", label: "Maior valor bruto" },
              { id: "inscritos_desc", label: "Mais inscritos" },
              { id: "nome_asc", label: "Nome (A-Z)" }
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setOrdenarPor(option.id)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer border ${
                  ordenarPor === option.id
                    ? "bg-urb-primary text-black border-urb-primary"
                    : "bg-urb-card hover:bg-urb-card-light text-slate-300 border-urb-border"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* Tabela de Dados */}
        <div className="overflow-x-auto w-full rounded-xl border border-urb-border/40">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-urb-border/60 bg-urb-card-light/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <th className="py-3 px-4">Corrida</th>
                <th className="py-3 px-4 text-center">Qtd. Inscritos</th>
                <th className="py-3 px-4 text-center">
                  <span>Valor Acumulado </span>
                  <span title="Total arrecadado bruto" className="text-slate-500 cursor-help">ⓘ</span>
                </th>
                <th className="py-3 px-4 text-center">
                  <span>Taxa URB RACE </span>
                  <span title="Valor da taxa definida para a corrida" className="text-slate-500 cursor-help">ⓘ</span>
                </th>
                <th className="py-3 px-4 text-center">
                  <span>Valor a Repassar </span>
                  <span title="Comissão de serviço" className="text-slate-500 cursor-help">ⓘ</span>
                </th>
                <th className="py-3 px-4 text-center">
                  <span>Valor Líquido </span>
                  <span title="Líquido a receber" className="text-slate-500 cursor-help">ⓘ</span>
                </th>
                <th className="py-3 px-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-urb-border/30">
              {aCarregar ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="py-3.5 px-4">
                      <div className="h-9 w-44 bg-slate-800 rounded-lg"></div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="h-6 w-12 bg-slate-800 rounded mx-auto"></div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="h-6 w-20 bg-slate-800 rounded mx-auto"></div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="h-6 w-16 bg-slate-800 rounded mx-auto"></div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="h-6 w-20 bg-slate-800 rounded mx-auto"></div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="h-6 w-20 bg-slate-800 rounded mx-auto"></div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="h-7 w-20 bg-slate-800 rounded mx-auto"></div>
                    </td>
                  </tr>
                ))
              ) : dadosPaginados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-500" />
                    <p className="text-sm">Nenhum evento financeiro encontrado.</p>
                  </td>
                </tr>
              ) : (
                dadosPaginados.map((row) => {
                  const visual = getRaceVisualIcon(row.nome_corrida);
                  const Icon = visual.Icon;
                  const usuarioLogadoId = Number(sessionStorage.getItem("brv-user-id"));
                  const donoId = row.id_usuario?.id ?? row.id_usuario;
                  const ehCoOrganizador = donoId && Number(donoId) !== usuarioLogadoId;
                  const taxa = getTaxaCorrida(row);
                  const ehPorcentagem = typeof taxa === "string" && taxa.includes("%");
                  const comissao = row.valor_repassar ?? ((Number(row.valor_acumulado) || 0) * 0.1);
                  const liquido = row.valor_liquido ?? ((Number(row.valor_acumulado) || 0) * 0.9);

                  return (
                    <tr
                      key={row.id_corrida || row.id || Math.random()}
                      className="hover:bg-urb-card-light/40 transition-colors"
                    >
                      {/* Corrida */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border overflow-hidden ${visual.bg}`}>
                            {row.imagem_url ? (
                              <img
                                src={row.imagem_url}
                                alt={row.nome_corrida || "Corrida"}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <Icon className={`w-5 h-5 ${visual.color}`} />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-white text-xs sm:text-sm">
                                {row.nome_corrida || "Corrida sem nome"}
                              </span>
                              {ehCoOrganizador && (
                                <Badge variant="info" size="sm">
                                  <Users className="w-3 h-3 mr-1 inline" />
                                  Co-organizador
                                </Badge>
                              )}
                            </div>
                            <span className="text-[11px] text-slate-400">
                              {row.data_evento ? formatDate(row.data_evento) : "Data a definir"}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Qtd. Inscritos */}
                      <td className="py-3.5 px-4 text-center font-medium text-slate-200">
                        {row.total_inscritos || 0}
                      </td>

                      {/* Valor Acumulado */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-bold text-white text-xs sm:text-sm">
                            {formatCurrency(row.valor_acumulado)}
                          </span>
                          <span className="text-[10px] text-slate-400">bruto</span>
                        </div>
                      </td>

                      {/* Taxa URB RACE */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-semibold text-slate-200 text-xs sm:text-sm">
                            {formatTaxa(taxa)}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {ehPorcentagem ? "porcentagem" : "por inscrição"}
                          </span>
                        </div>
                      </td>

                      {/* Valor a Repassar */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-bold text-blue-400 text-xs sm:text-sm">
                            {formatCurrency(comissao)}
                          </span>
                          <span className="text-[10px] text-slate-400">comissão</span>
                        </div>
                      </td>

                      {/* Valor Líquido */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-bold text-urb-primary text-xs sm:text-sm">
                            {formatCurrency(liquido)}
                          </span>
                          <span className="text-[10px] text-slate-400">líquido</span>
                        </div>
                      </td>

                      {/* Ações */}
                      <td className="py-3.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => abrirDetalhesCorrida(row)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-urb-card-light hover:bg-urb-card border border-urb-border hover:border-urb-primary/60 text-slate-300 hover:text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                        >
                          <span>Ver detalhes</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer da Tabela com Paginação Customizada */}
        {!aCarregar && dadosFiltrados.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 mt-3 border-t border-urb-border/40">
            <span className="text-xs text-slate-400">
              Mostrando {inicioItem} a {fimItem} de {totalMostrado} corridas
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                disabled={paginaAtual === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  type="button"
                  onClick={() => setPaginaAtual(pg)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-colors cursor-pointer border ${
                    pg === paginaAtual
                      ? "bg-urb-primary text-black border-urb-primary"
                      : "bg-urb-card-light border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/40"
                  }`}
                >
                  {pg}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))}
                disabled={paginaAtual === totalPaginas}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Detalhes da Corrida */}
      <Modal
        header={corridaSelecionada ? `Financeiro - ${corridaSelecionada.nome_corrida}` : "Detalhes da Corrida"}
        visible={detalhesDialog}
        onHide={() => setDetalhesDialog(false)}
        className="max-w-2xl"
      >
        {corridaSelecionada && (
          <div className="flex flex-col gap-4 text-urb-text">
            {/* Resumo de Inscritos */}
            <div className="bg-urb-card-light/70 border border-urb-border rounded-xl p-4">
              <span className="text-xs text-slate-400">Total de Inscritos</span>
              <h3 className="text-xl font-bold text-white mt-1">
                {corridaSelecionada.total_inscritos || 0} inscritos confirmados
              </h3>
            </div>

            {/* Cards de Valores */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-urb-card-light/70 border border-urb-border rounded-xl p-4">
                <span className="text-xs text-slate-400">Valor Acumulado</span>
                <p className="text-lg font-bold text-white mt-1">
                  {formatCurrency(corridaSelecionada.valor_acumulado)}
                </p>
              </div>

              <div className="bg-urb-card-light/70 border border-urb-border rounded-xl p-4">
                <span className="text-xs text-slate-400">
                  Comissão URB RACE ({formatTaxa(getTaxaCorrida(corridaSelecionada))})
                </span>
                <p className="text-lg font-bold text-blue-400 mt-1">
                  {formatCurrency(corridaSelecionada.valor_repassar ?? ((Number(corridaSelecionada.valor_acumulado) || 0) * 0.1))}
                </p>
              </div>
            </div>

            {/* Valor Líquido */}
            <div className="bg-urb-primary/10 border border-urb-primary/30 rounded-xl p-4">
              <span className="text-xs font-semibold text-urb-primary">
                Valor Líquido a Receber
              </span>
              <h2 className="text-2xl font-black text-urb-primary mt-1">
                {formatCurrency(corridaSelecionada.valor_liquido ?? ((Number(corridaSelecionada.valor_acumulado) || 0) * 0.9))}
              </h2>
            </div>

            {/* Meios de Pagamento */}
            {meiosPagamento && (
              <div className="bg-urb-card-light/70 border border-urb-border rounded-xl p-4">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">
                  Meios de Pagamento
                </span>
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-urb-primary">PIX:</span>
                    <span className="text-sm font-semibold text-white">{meiosPagamento.pix || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-blue-400">Cartão:</span>
                    <span className="text-sm font-semibold text-white">{meiosPagamento.cartao || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-amber-400">Boleto:</span>
                    <span className="text-sm font-semibold text-white">{meiosPagamento.boleto || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tabela de Inscrições Recentes */}
            {aCarregarDetalhes ? (
              <div className="h-20 w-full bg-slate-800 rounded-xl animate-pulse"></div>
            ) : inscricoesDetalhes.length > 0 && (
              <div className="mt-1">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">
                  Últimas Inscrições
                </span>
                <div className="overflow-x-auto rounded-xl border border-urb-border/50">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-urb-border bg-urb-card-light/50 text-slate-400">
                        <th className="py-2.5 px-3">Nome</th>
                        <th className="py-2.5 px-3">Status</th>
                        <th className="py-2.5 px-3">Data</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-urb-border/30">
                      {inscricoesDetalhes.slice(0, 5).map((insc, idx) => (
                        <tr key={insc.id || idx} className="hover:bg-urb-card-light/30">
                          <td className="py-2.5 px-3 text-white font-medium">
                            {`${insc.first_name || ""} ${insc.last_name || ""}`}
                          </td>
                          <td className="py-2.5 px-3 text-slate-300">
                            {insc.status || "Confirmado"}
                          </td>
                          <td className="py-2.5 px-3 text-slate-400">
                            {formatDate(insc.data_inscricao)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default FinanceiroOrganizador;